import React, { useState, useEffect } from 'react';
import Sidebar from './Components/Sidebar/Sidebar';
import Layout from './Components/Layout/Layout';
import LoginPage from './Components/LoginPage/LoginPage'; // ADD THIS IMPORT
import './App.css';
import axiosInstance from './lib/axiosInstance';
import { getStoredBranchId, setStoredBranchId } from './lib/branch';
import {
  getStoredUser,
  setStoredUser,
  MENU_LABEL_TO_PATH,
  firstAllowedMenuLabel,
  canAccessMenuLabel,
} from './lib/permissions';

const pathToMenu = {
  dashboard: 'Dashboard',
  appointments: 'Appointments',
  billing: 'Billing',
  patientrecords: 'PatientRecords',
  schedule: 'Schedule',
  revenuereports: 'RevenueReports',
  settings: 'Settings',
};

const menuToPath = MENU_LABEL_TO_PATH;

function getMenuFromPath(pathname) {
  const slug = String(pathname || '/').replace(/^\//, '').toLowerCase();
  return pathToMenu[slug] || 'Dashboard';
}

function App() {
  const [activeMenu, setActiveMenu] = useState('Dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(() => getStoredUser());
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(getStoredBranchId());

  // ✅ CHECK AUTH ON APP LOAD + refresh user (permissions) from API
  useEffect(() => {
    const authToken = localStorage.getItem('isAuthenticated');
    setIsAuthenticated(!!authToken);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await axiosInstance.get('/auth/me');
        const u = res.data?.user ?? res.data;
        if (mounted && u) {
          setUser(u);
          setStoredUser(u);
        }
      } catch {
        const fallback = getStoredUser();
        if (mounted) {
          setUser(
            fallback || {
              role: 'doctor',
              permissions: [],
              permissionsExplicit: false,
            }
          );
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const syncFromPath = () => setActiveMenu(getMenuFromPath(window.location.pathname));
    syncFromPath();
    window.addEventListener('popstate', syncFromPath);
    return () => window.removeEventListener('popstate', syncFromPath);
  }, []);

  useEffect(() => {
    if (!user || !isAuthenticated) return;
    if (!canAccessMenuLabel(user, activeMenu)) {
      const allowed = firstAllowedMenuLabel(user);
      if (allowed) {
        setActiveMenu(allowed);
        const path = menuToPath[allowed] || 'dashboard';
        window.history.replaceState({}, '', `/${path}`);
      }
    }
  }, [user, isAuthenticated, activeMenu]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let mounted = true;
    async function loadBranches() {
      try {
        const res = await axiosInstance.get('/branches', { params: { isActive: true, page: 1, limit: 100 } });
        const data = res && res.data ? res.data : res;
        const list = data && data.items ? data.items : (Array.isArray(data) ? data : []);
        if (!mounted) return;
        setBranches(list);
        const selectedExists = list.some((b) => (b._id || b.id) === selectedBranchId);
        if ((!selectedBranchId || !selectedExists) && list.length > 0) {
          const defaultId = list[0]._id || list[0].id;
          setSelectedBranchId(defaultId);
          setStoredBranchId(defaultId);
        }
      } catch (err) {
        if (mounted) setBranches([]);
      }
    }
    loadBranches();
    return () => { mounted = false; };
  }, [isAuthenticated, selectedBranchId]);

  // ✅ LOGOUT FUNCTION
  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    setStoredUser(null);
    localStorage.removeItem('token');
    setStoredBranchId('');
    window.location.href = '/'; // or window.location.href = '/';
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranchId(branchId);
    setStoredBranchId(branchId);
  };

  const handleNavigate = (menu) => {
    if (user && !canAccessMenuLabel(user, menu)) {
      const allowed = firstAllowedMenuLabel(user);
      if (allowed) {
        setActiveMenu(allowed);
        window.history.pushState({}, '', `/${menuToPath[allowed] || 'dashboard'}`);
      }
      return;
    }
    setActiveMenu(menu);
    const path = menuToPath[menu] || 'dashboard';
    window.history.pushState({}, '', `/${path}`);
  };

  // Show login if not authenticated
  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="app">
      <Sidebar
        user={user}
        onMenuClick={handleNavigate}
        activeMenu={activeMenu}
        onLogout={handleLogout}
      />
      <Layout
        user={user}
        activeMenu={activeMenu}
        onNavigate={handleNavigate}
        branches={branches}
        selectedBranchId={selectedBranchId}
        onBranchChange={handleBranchChange}
      />
    </div>
  );
}

export default App;
